'use client'

// Animation
import { motion } from 'framer-motion'
import { Container, fadeInUp, Section, staggerContainer } from 'design-studio'

// Layout
// UI components
import { SectionHeader } from '@/components/ui/section-header'
import { TerminalBox } from '@/components/ui/terminal-box'
import {
  CommandLineIcon,
  CubeTransparentIcon,
  ServerIcon,
} from '@heroicons/react/24/outline'

type TerminalToken = { text: string; color: string }

const INTERFACES: {
  id: string
  title: string
  description: string
  terminal: { copy: string; tokens: TerminalToken[] }
  link: { label: string; href: string }
  icon: React.ReactNode
  accent: string
}[] = [
  {
    id: 'vscode',
    title: 'VS Code, Cursor & Antigravity Extension',
    icon: <CommandLineIcon className="h-5 w-5 text-cyan-400" />,
    accent: 'border-cyan-500/30 hover:border-cyan-400/50',
    description:
      'Goal-state visualization, incremental proof checking, syntax highlighting and type checking via LSP. Works out of the box in Cursor and Antigravity. Install with one command or search "ImandraX" in the Extensions panel.',
    terminal: {
      copy: 'code --install-extension imandra.imandrax',
      tokens: [
        { text: 'code', color: 'text-cyan-400' },
        { text: ' ', color: '' },
        { text: '--install-extension', color: 'text-slate-400' },
        { text: ' ', color: '' },
        { text: 'imandra.imandrax', color: 'text-teal-300' },
      ],
    },
    link: {
      label: 'VS Code Marketplace',
      href: 'https://marketplace.visualstudio.com/items?itemName=imandra.imandrax',
    },
  },
  {
    id: 'python',
    title: 'Python Library',
    icon: <CubeTransparentIcon className="h-5 w-5 text-teal-400" />,
    accent: 'border-teal-500/30 hover:border-teal-400/50',
    description:
      'Access virtually every feature of ImandraX with native Python support. Prove properties, synthesize counterexamples, and decompose state-spaces programmatically — ideal for notebooks, CI pipelines, and agent workflows.',
    terminal: {
      copy: 'pip install imandrax-api',
      tokens: [
        { text: 'pip', color: 'text-cyan-400' },
        { text: ' ', color: '' },
        { text: 'install', color: 'text-blue-400' },
        { text: ' ', color: '' },
        { text: 'imandrax-api', color: 'text-teal-300' },
      ],
    },
    link: {
      label: 'PyPI package',
      href: 'https://pypi.org/project/imandra/',
    },
  },
  {
    id: 'mcp',
    title: 'MCP Server',
    icon: <ServerIcon className="h-5 w-5 text-blue-400" />,
    accent: 'border-blue-500/30 hover:border-blue-400/50',
    description:
      'Use ImandraX via its own Model Context Protocol server. AI assistants and coding agents (Claude, GPT, Cursor, Gemini) can invoke ImandraX directly — proving properties and finding counterexamples from within a conversation.',
    terminal: {
      copy: '{"mcpServers":{"imandrax":{"url":"https://universe.imandra.ai/reasoners/imandrax/mcp/sse"}}}',
      tokens: [
        { text: '{', color: 'text-slate-400' },
        { text: '"mcpServers"', color: 'text-teal-300' },
        { text: ': { ', color: 'text-slate-400' },
        { text: '"imandrax"', color: 'text-blue-400' },
        { text: ':', color: 'text-slate-400' },
        { text: ' { "url"', color: 'text-teal-300' },
        { text: ': "…/mcp/sse" } } }', color: 'text-slate-400' },
      ],
    },
    link: {
      label: 'MCP setup guide',
      href: 'https://universe.imandra.ai/reasoners/imandrax/mcp',
    },
  },
]

export function Interfaces() {
  return (
    <Section
      id="interfaces"
      className="relative scroll-mt-32 py-16 sm:pt-24 sm:pb-32"
    >
      <Container className="relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
          className="space-y-10"
        >
          <motion.div variants={fadeInUp} className="mx-auto max-w-6xl">
            <SectionHeader
              pretitle="Interfaces"
              title={
                <>
                  Use ImandraX <br /> Your Way
                </>
              }
              description="ImandraX meets you where you are — in VS Code, in Python, or via MCP from any AI assistant."
              pretitleColor="text-cyan-500/80"
            />
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="max-w-8xl mx-auto grid gap-6 md:grid-cols-3"
          >
            {INTERFACES.map((iface) => (
              <div
                key={iface.id}
                className={`rounded-xl border bg-slate-900/40 p-6 backdrop-blur-md transition-all duration-200 hover:bg-slate-900/50 ${iface.accent}`}
              >
                <div className="mb-3 flex items-center gap-2">{iface.icon}</div>
                <h3 className="font-display mb-3 text-2xl font-light tracking-tight text-white">
                  {iface.title}
                </h3>
                <p className="font-display mb-4 text-base leading-relaxed font-light text-slate-300">
                  {iface.description}
                </p>
                <TerminalBox
                  copyContent={iface.terminal.copy}
                  className="dark:border-slate-600/40 dark:bg-slate-900/80"
                >
                  <span className="my-1 block">
                    {iface.terminal.tokens.map((token, i) => (
                      <span key={i} className={token.color}>
                        {token.text}
                      </span>
                    ))}
                  </span>
                </TerminalBox>
                <a
                  href={iface.link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 font-mono text-sm text-blue-400 underline-offset-2 transition-colors hover:text-blue-300 hover:underline"
                >
                  {iface.link.label} →
                </a>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </Container>
    </Section>
  )
}
